import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Platform } from "@marketing/shared";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export type AvailablePage = {
  externalId: string;
  name: string;
  avatarUrl: string | null;
  platforms: Platform[];
  alreadyConnected: boolean;
};

export type ConnectedAccount = {
  id: string;
  platform: Platform;
  externalId: string;
  displayName: string;
  avatarUrl: string | null;
  status: "active" | "expiring" | "disconnected";
  tokenExpiresAt: string | null;
  createdAt: string;
  brand: { id: string; name: string };
};

export type PageAssignment = {
  externalId: string;
  brandId: string;
  platforms: Platform[];
};

/** Asks the API for the Meta consent URL, then hands the browser over to it. */
/**
 * Threads, which needs a brand up front.
 *
 * Meta can take an optional brand because its picker asks afterwards. Threads
 * authorises exactly one profile and shows no picker, so without a brand the
 * callback would have a connected profile belonging to nothing — the API
 * refuses it, and the button is disabled until one is chosen.
 */
export function useStartThreadsConnect() {
  return useMutation({
    mutationFn: async (brandId: string) => {
      const { authorizeUrl } = await unwrap<{ authorizeUrl: string }>(
        api.get(`/social/threads/start?brandId=${encodeURIComponent(brandId)}`),
      );
      return authorizeUrl;
    },
    onSuccess: (url) => window.location.assign(url),
  });
}

export function useStartMetaConnect() {
  return useMutation({
    mutationFn: async (brandId?: string) => {
      const q = brandId ? `?brandId=${encodeURIComponent(brandId)}` : "";
      const { authorizeUrl } = await unwrap<{ authorizeUrl: string }>(
        api.get(`/social/meta/start${q}`),
      );
      return authorizeUrl;
    },
    onSuccess: (url) => {
      // Full navigation, not a popup: Facebook blocks its consent screen in
      // many popup contexts, and the callback redirects back here anyway.
      window.location.assign(url);
    },
  });
}

export function useAvailablePages(connectionId: string | null) {
  return useQuery({
    queryKey: ["social", "pages", connectionId],
    queryFn: () => unwrap<AvailablePage[]>(api.get(`/social/connections/${connectionId}/pages`)),
    enabled: !!connectionId,
    // The connection is single-use and short-lived; a refetch after it is
    // consumed would 400, so don't retry or refetch in the background.
    retry: false,
    staleTime: Infinity,
  });
}

export function useConnectedAccounts() {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["social", "accounts", workspaceId],
    queryFn: () => unwrap<ConnectedAccount[]>(api.get("/social/accounts")),
    enabled: !!workspaceId,
  });
}

/** Corrects a page connected to the wrong brand, without a reconnect. */
export function useMovePageBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { accountId: string; brandId: string }) =>
      unwrap<ConnectedAccount[]>(
        api.patch(`/social/accounts/${args.accountId}/brand`, { brandId: args.brandId }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social", "accounts"] });
      // Which brand has a page decides the setup sequence and the pickers.
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useAssignPages(connectionId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignments: PageAssignment[]) =>
      unwrap<{ connected: number }>(
        api.post(`/social/connections/${connectionId}/assign`, { assignments }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social", "accounts"] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export type DisconnectResult = {
  /** "deleted" when nothing of value went with it, "archived" when history did. */
  outcome: "deleted" | "archived";
  /** Drafts removed because they had nowhere left to publish. */
  draftsRemoved: number;
};

export function useDisconnectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<DisconnectResult>(api.delete(`/social/accounts/${id}`)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social", "accounts"] });
      // Losing a page changes what the calendar holds, how many pages count
      // against the plan, and whether the "connect a page" step is done.
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["approvals"] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}
